/**
 * ColorPicker - reusable swatch-based colour picker.
 *
 * A small, controlled colour selector: a grid of preset swatches plus an
 * optional custom hex input. Built to be reused anywhere the platform needs
 * a colour choice (label colours, status pills, category accents, etc.).
 *
 * The swatch fill is a runtime-dynamic colour value (from data or a custom
 * hex the user types), so the background colour is applied via inline style -
 * the one sanctioned exception for values that cannot be known at build time.
 * All other styling uses Tailwind utilities and shared primitives.
 */

import { Check } from 'lucide-react'

import { Button } from '@/shared/components/ui/button'
import { Input } from '@/shared/components/ui/input'
import { cn } from '@/shared/lib/utils'

export interface ColorPickerProps {
  /** Currently selected colour (hex, e.g. `#16a34a`). */
  value?: string | null
  /** Called with the new hex when a swatch is picked or hex typed. */
  onChange: (color: string) => void
  /** Preset swatches. Defaults to {@link DEFAULT_COLOR_PRESETS}. */
  presets?: ColorPreset[]
  /** Allow a free-form hex entry below the swatches. Defaults to true. */
  allowCustom?: boolean
  className?: string
  /** Disable interaction. */
  disabled?: boolean
}

export interface ColorPreset {
  name: string
  value: string
}

/** A calm, label-friendly palette that reads well on light surfaces. */
// eslint-disable-next-line react-refresh/only-export-components
export const DEFAULT_COLOR_PRESETS: ColorPreset[] = [
  { name: 'Slate', value: '#64748b' },
  { name: 'Red', value: '#ef4444' },
  { name: 'Orange', value: '#f97316' },
  { name: 'Amber', value: '#f59e0b' },
  { name: 'Green', value: '#16a34a' },
  { name: 'Teal', value: '#14b8a6' },
  { name: 'Sky', value: '#0ea5e9' },
  { name: 'Blue', value: '#3b82f6' },
  { name: 'Indigo', value: '#6366f1' },
  { name: 'Purple', value: '#a855f7' },
  { name: 'Pink', value: '#ec4899' },
  { name: 'Stone', value: '#78716c' },
]

const HEX_RE = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/

/** True when `value` is a valid 3- or 6-digit hex colour. */
// eslint-disable-next-line react-refresh/only-export-components
export function isValidHexColor(value: string | null | undefined): boolean {
  return typeof value === 'string' && HEX_RE.test(value.trim())
}

/**
 * ColorSwatch - a read-only colour dot, for tables, chips and previews.
 * Falls back to a neutral outline when no colour is set.
 */
export function ColorSwatch({
  color,
  className,
}: {
  color?: string | null
  className?: string
}) {
  const valid = isValidHexColor(color)
  return (
    <span
      className={cn(
        'inline-block h-3.5 w-3.5 shrink-0 rounded-full ring-1 ring-inset ring-black/10',
        !valid && 'bg-[var(--bg-subtle)]',
        className,
      )}
      style={valid ? { backgroundColor: (color as string).trim() } : undefined}
    />
  )
}

export function ColorPicker({
  value,
  onChange,
  presets = DEFAULT_COLOR_PRESETS,
  allowCustom = true,
  className,
  disabled = false,
}: ColorPickerProps) {
  const current = (value ?? '').trim()

  return (
    <div className={cn('flex flex-col gap-3', className)}>
      <div className="flex flex-wrap gap-1.5">
        {presets.map((preset) => {
          const selected = current.toLowerCase() === preset.value.toLowerCase()
          return (
            <Button
              key={preset.value}
              type="button"
              variant="ghost"
              size="icon-sm"
              disabled={disabled}
              title={preset.name}
              aria-label={preset.name}
              aria-pressed={selected}
              onClick={() => onChange(preset.value)}
              className={cn(
                'relative rounded-full p-0 hover:bg-transparent',
                selected && 'ring-2 ring-[var(--green)] ring-offset-1',
              )}
            >
              <span
                className="block h-5 w-5 rounded-full ring-1 ring-inset ring-black/10"
                style={{ backgroundColor: preset.value }}
              />
              {selected ? (
                <Check className="absolute inset-0 m-auto h-3 w-3 text-white drop-shadow" />
              ) : null}
            </Button>
          )
        })}
      </div>

      {allowCustom ? (
        <div className="flex items-center gap-2">
          <ColorSwatch color={current} />
          <Input
            value={current}
            disabled={disabled}
            placeholder="#16a34a"
            spellCheck={false}
            autoComplete="off"
            onChange={(event) => onChange(event.target.value.trim())}
            className="h-8 max-w-[140px] font-mono text-[12px]"
          />
        </div>
      ) : null}
    </div>
  )
}
